"use client";
import styles from "./MMVHeroSection.module.scss";
import { SplitLayout } from "@components/molecules/SplitLayout";
import ColorPicker from "@components/molecules/ColorPicker";
import Button from "@components/atoms/Button";
import { getMultiToneGradientColor } from "@helpers/getMultiToneGradientColor";
import getIndianCurrencyFormat, {
  getIndianCurrencyFormatForMaskedPrice,
} from "@helpers/getIndianCurrencyFormat";
import { addCaptureIntentIfNeeded, getLoginUrl } from "@utils/getLoginUrl";
import { toKebabCase } from "@utils/stringFormatters";
import ImageResponsive from "@components/atoms/ImageResponsive";
import { useMemo, useEffect, useRef } from "react";
import { getCarsCount } from "@helpers/getCarsCount";
import { convertDate } from "@helpers/getDeliveryDateFormat";
import { PageType } from "@constants/pageTypes";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";
import Image from "next/image";
import assetLoader from "@utils/internalImageLoder";
import TrustMarkerBadge from "@components/atoms/TrustMarkerBadge";
import AckoDriveAwardsClient from "@components/molecules/AckoDriveAwards/AckoDriveAwards.client";
import { AwardTrustMarker } from "@interfaces/trust-markers.types";
import { LoginSdkConfig } from "@enums/LoginSdkConfig";
import { handleLoginFlow } from "@utils/loginFlowHandler";
import { formatPrice } from "@utils/numberFormatter";
// import { getMaskedPricing } from "@helpers/maskPricing";
// import { getDeliveryDate } from "@helpers/getDeliveryDate";

type Color = {
  id: string;
  // hexCodes: string[];
  base_colors_hexcodes: string[];
  name: string;
  brand_color: string;
  pricing: any;
  deliveryOptions: any;
  type: string;
  Inventory_details: any;
  Adsp_details: any;
  hero_image: string;
};

type MMVHeroSectionProps = {
  mmvName: string;
  brandName: string;
  modelName: string;
  variantName: string;
  colors: Color[];
  selectedColor: Color;
  seatingCapacity: number;
  transmission: string;
  fuelType: string;
  price: any;
  isLoggedIn: boolean;
  isInventoryAvailable: boolean;
  deliveryOptions?: any;
  selectedDelivery: any;
  setIsModalOpen: (val: boolean) => void;
  setIsIntentModalOpen: (val: boolean) => void;
  cityName: string;
  inventoryAlertData: {
    alert_type: string;
    alert_units: number;
  };
  onCtaVisibilityChange?: (isVisible: boolean) => void;
  isSellable: boolean;
  pageType?: PageType;
  handleGetQuote: () => void;
  carAvailability?: string;
  trustMarkerBadge?: string | null;
  awardTrustMarkers?: AwardTrustMarker[];
  showAwards?: boolean;
  selectedYear: string;
  onYearChange: (year: string) => void;
  availableYears?: string[];
  priceData: any
};

// const DELIVERY_TYPE_LABEL = {
//   express: "Express Delivery",
//   standard: "Standard Delivery",
// };
const DELIVERY_TYPE_LABEL = {
  inventory: "Express Delivery",
  adsp: "Standard Delivery",
};

export default function MMVHeroSection({
  mmvName,
  brandName,
  modelName,
  variantName,
  colors,
  selectedColor,
  seatingCapacity,
  transmission,
  fuelType,
  isLoggedIn,
  isInventoryAvailable,
  selectedDelivery,
  setIsModalOpen,
  setIsIntentModalOpen,
  cityName,
  inventoryAlertData,
  handleGetQuote,
  onCtaVisibilityChange,
  isSellable,
  pageType,
  carAvailability,
  awardTrustMarkers,
  showAwards,
  trustMarkerBadge,
  selectedYear,
  onYearChange,
  availableYears,
  priceData
}: MMVHeroSectionProps) {
  const {
    market_price: onRoadPrice,
    adsp_price: adspPrice,
    deal_type: deliveryOption,
    waiting_period: deliveryDate,
    savings_breakup: savingsBreakup,
  } = priceData || {};
  const deliveryType =
    deliveryOption === "Inventory"
      ? DELIVERY_TYPE_LABEL.inventory
      : DELIVERY_TYPE_LABEL.adsp;
  const colorName = selectedColor?.brand_color || "";
  // const deliveryDate = getDeliveryDate(deliveryDate ? Number(deliveryDate) : 0);
  const alertData: {
    alertUnit?: number;
    alertText?: string;
  } = {
    alertUnit: inventoryAlertData?.alert_units,
    alertText: inventoryAlertData?.alert_type,
  };

  const getRIPPlusConsumer = (savingsBreakup: { [key: string]: number }) => {
    return savingsBreakup?.ripdiscount + savingsBreakup?.consumerdiscount || 0;
  };

  const alertCarUnits = useMemo(
    () => getCarsCount(alertData?.alertUnit ?? null),
    [alertData.alertUnit]
  );

  const ctaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!onCtaVisibilityChange) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        onCtaVisibilityChange(entry.isIntersecting);
      },
      {
        threshold: 0.1,
        rootMargin: "0px 0px 0px 0px",
      }
    );

    if (ctaRef.current) {
      observer.observe(ctaRef.current);
    }

    return () => {
      if (ctaRef.current) {
        observer.unobserve(ctaRef.current);
      }
    };
  }, [onCtaVisibilityChange]);

  const handleYearClick = (year: string) => {
    if (year === selectedYear) return;
    onYearChange(year);
  };

  return (
    <SplitLayout>
      {/* Car section */}
      <div className={styles.carSection}>
        <div className={styles.backgroundWrapper} />
        <div className={styles.gradientOverlayTop} />
        <div className={styles.gradientOverlayBottom} />

        <div className={styles.carContent}>
          {/* Mobile view header */}
          <div className={styles.mobileHeader}>
            <h1 className={styles.carTitle}>{mmvName}</h1>
            <p className={styles.carSpecs}>
              {`${fuelType} • ${transmission} • ${seatingCapacity || 0}`}
            </p>
          </div>

          <div className={styles.carImageWrapper}>
            <ImageResponsive
              filePath={`${toKebabCase(brandName)}/${toKebabCase(
                modelName
              )}/${toKebabCase(colorName)}`}
              imageTag={`Transparent`}
              alt={`${brandName} ${modelName}`}
              width={700}
              height={400}
              className={styles.carImage}
              style={{ margin: "0 auto", display: "block" }}
              priority
            />
          </div>

          <div className={styles.mobileColorPicker}>
            <ColorPicker
              colors={colors}
              selectedColor={selectedColor}
              brandName={brandName}
              modelName={modelName}
              variantName={variantName}
              pageType={pageType}
              isSellable={isSellable}
              carAvailability={carAvailability}
              colorButtonClassname={styles.colorButton}
              colorListItemClassname={styles.colorButton}
            />
          </div>

          {/* Desktop view color badge */}
          <div className={styles.colorBadgeWrapper}>
            <div className={styles.colorBadge}>
              <span
                className={styles.colorDot}
                style={{
                  background: getMultiToneGradientColor(
                    colors?.find((c) => c.id === selectedColor?.id)
                      ?.base_colors_hexcodes ?? []
                  ),
                }}
              ></span>
              <span>{selectedColor?.brand_color}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Details section */}
      <div className={styles.detailsSection} ref={ctaRef}>
        {/* Desktop view header */}
        <div className={styles.desktopInfo}>
          {trustMarkerBadge && (
            <div className={styles.desktopTrustMarker}>
              <TrustMarkerBadge label={trustMarkerBadge} />
            </div>
          )}

          <div className={styles.desktopHeader}>
            <h1 className={styles.carTitle}>{mmvName}</h1>
            <p className={styles.carSpecs}>
              {`${fuelType} • ${transmission} • ${seatingCapacity || 0}`}
              {/* Seater */}
            </p>
          </div>

          <ColorPicker
            colors={colors}
            selectedColor={selectedColor}
            brandName={brandName}
            modelName={modelName}
            variantName={variantName}
            pageType={pageType}
            isSellable={isSellable}
            carAvailability={carAvailability}
            colorButtonClassname={styles.colorButton}
            colorListItemClassname={styles.colorButton}
          />
        </div>

        <div className={styles.priceSection}>
          {trustMarkerBadge && (
            <div className={styles.mobileTrustMarker}>
              <TrustMarkerBadge label={trustMarkerBadge} />
            </div>
          )}

          <div className={styles.priceHeaderContainer}>
            {availableYears && availableYears.length > 1 && (
              <div className={styles.priceHeaderYears}>
                {availableYears.map((year) => (
                  <div
                    className={`${styles.priceHeaderYear} ${
                      year === selectedYear ? styles.priceHeaderYearActive : ""
                    }`}
                    key={year}
                    onClick={() => handleYearClick(year)}
                  >
                    {year} Model
                  </div>
                ))}
              </div>
            )}
            <div className={styles.priceHeaderContent}>
              <div className={styles.priceHeader}>
                {!isSellable && <span>{`On-road price in ${cityName}`}</span>}
                {isSellable && (
                  <span>
                    {`${
                      !isLoggedIn ? "On-road" : "ACKO Drive"
                    } price in ${cityName}`}
                  </span>
                )}
              </div>
              {isSellable && (
                <div className={styles.priceAmount}>
                  <h2>
                    {`${formatPrice(isLoggedIn ? adspPrice : onRoadPrice)}`}
                  </h2>
                  {isLoggedIn && (
                    <span className={styles.onRoadPriceText}>
                      {`${formatPrice(
                        onRoadPrice - getRIPPlusConsumer(savingsBreakup)
                      )}`}
                    </span>
                  )}
                  <p className={styles.discountText}>
                    {`Save ₹${
                      isLoggedIn
                        ? getIndianCurrencyFormat(
                            savingsBreakup?.ackodiscount || 0
                          )
                        : getIndianCurrencyFormatForMaskedPrice(
                            savingsBreakup?.ackodiscount || ""
                          )
                    }`}
                  </p>
                </div>
              )}
              {!isSellable && (
                <div className={styles.priceAmount}>
                  <h2>{`${formatPrice(onRoadPrice)}`}</h2>
                </div>
              )}
            </div>
          </div>
          {isSellable && (
            <div className={styles.deliveryHeader}>
              {isInventoryAvailable && isLoggedIn ? (
                <p
                  className={styles.fasterDelivery}
                  onClick={() => setIsModalOpen(true)}
                >
                  {deliveryType === DELIVERY_TYPE_LABEL.inventory && (
                    <Image
                      src={`${assetLoader()}/icons/HeroSection/ExpressDeliveryPurple.svg`}
                      alt="Express Delivery"
                      width={16}
                      height={16}
                      className={styles.zapIcon}
                    />
                  )}
                  <span
                    className={`${
                      deliveryType === DELIVERY_TYPE_LABEL.inventory
                        ? styles.expressText
                        : ""
                    }`}
                  >
                    {deliveryType} by
                  </span>
                  &nbsp;
                  <span>{convertDate(deliveryDate)}</span>
                  <Image
                    src={`${assetLoader()}/icons/Edit_Pencil.svg`}
                    alt="Edit"
                    width={15}
                    height={15}
                    className={styles.editIcon}
                  />
                </p>
              ) : (
                <span>{`${deliveryType} by ${convertDate(deliveryDate)}`}</span>
              )}
            </div>
          )}
        </div>
        {isSellable && (
          <div className={styles.ctaSection}>
            {alertData.alertUnit && (
              <div className={styles.stockAlert}>
                {alertData.alertText === "PRICE_ALERT" ? (
                  <span>
                    {`PRICE ALERT! Only ${alertCarUnits} left at this price`}
                  </span>
                ) : (
                  alertData.alertText === "EXPRESS_DELIVERY_ALERT" && (
                    <span>{`Only ${alertCarUnits} left at this price`}</span>
                  )
                )}
              </div>
            )}
            <Button
              className={styles.unlockPriceButton}
              onClick={async () => {
                if (isLoggedIn) {
                  await trackEventWithCommonProps(
                    "action",
                    EventType.TRACK_EVENT,
                    {
                      pageType: pageType,
                      additionalProps: {
                        action_type: "tap_btn",
                        action_name: "tap_btn_get_detailed_quote",
                        car_availability: carAvailability,
                        vehicle_make: brandName,
                        vehicle_model: modelName,
                        vehicle_variant: variantName,
                        city_name: cityName,
                        section: "Top fold",
                      },
                    }
                  );
                  handleGetQuote();
                } else {
                  await trackEventWithCommonProps(
                    "action",
                    EventType.TRACK_EVENT,
                    {
                      pageType: pageType,
                      additionalProps: {
                        action_type: "tap_btn",
                        action_name: "tap_btn_login_to_save",
                        car_availability: carAvailability,
                        vehicle_make: brandName,
                        vehicle_model: modelName,
                        vehicle_variant: variantName,
                        city_name: cityName,
                        section: "Top fold",
                      },
                    }
                  );
                  const mmvDetails = {
                    make: brandName || "",
                    model: modelName || "",
                    varaint: variantName || "",
                    color: colorName || "",
                  };
                  if (typeof window !== "undefined") {
                    sessionStorage.setItem("loginType", "Reveal price");
                    localStorage.setItem(
                      "MMV",
                      JSON.stringify({
                        make: brandName || "",
                        model: modelName || "",
                        variant: variantName || "",
                        color: colorName || "",
                      })
                    );
                  }
                  const loginUrl = getLoginUrl(
                    addCaptureIntentIfNeeded(
                      window.location.href,
                      mmvDetails,
                      "Reveal price",
                      pageType
                    )
                  );
                  await handleLoginFlow(loginUrl, LoginSdkConfig.AD_PRICE);
                }
              }}
            >
              {!isLoggedIn ? "Unlock ACKO Drive price" : "Get a quote"}
            </Button>
            {isInventoryAvailable && !isLoggedIn && (
              <p className={styles.fasterDelivery}>
                <Image
                  src={`${assetLoader()}/icons/HeroSection/ExpressDeliveryPurple.svg`}
                  alt="Express Delivery"
                  width={16}
                  height={16}
                  className={styles.zapIcon}
                />
                <span>Express delivery available</span>
              </p>
            )}
          </div>
        )}

        {showAwards && awardTrustMarkers && awardTrustMarkers?.length > 0 && (
          <AckoDriveAwardsClient
            awards={awardTrustMarkers}
            className={styles.ackoDriveAwardsSection}
          />
        )}
      </div>
    </SplitLayout>
  );
}